import Icon from "@/components/mnf/Icon";
import { GlassCard } from "@/components/mnf/UI";
import type { SafetyAdvisory, SeasonState } from "@/lib/store";

/**
 * SafetyAdvisoryCard
 *
 * Full-detail card shown on RecipeDetail when a recipe carries season-relevant
 * safety flags. Lists each advisory with its plain explanation and a gentle
 * alternative, prefaced by Kiley's voice. Coach Kiley's plan and a sister's
 * provider always override; this is a heads-up, not a verdict.
 */
export default function SafetyAdvisoryCard({
  advisories,
  season,
}: {
  advisories: SafetyAdvisory[];
  season: SeasonState | null;
}) {
  if (advisories.length === 0 || !season) return null;

  const topSeverity = advisories.some((a) => a.severity === "avoid")
    ? "avoid"
    : "caution";

  const seasonLabel =
    season.kind === "pregnant" ? "this pregnancy"
      : season.kind === "nursing" ? "while nursing"
      : season.kind === "postpartum" ? "your postpartum season"
      : season.kind === "ttc" ? "this TTC season"
      : "this season";

  const headline =
    topSeverity === "avoid"
      ? `Worth setting aside ${seasonLabel}.`
      : `A gentle heads-up for ${seasonLabel}.`;

  const accent =
    topSeverity === "avoid"
      ? { line: "var(--mnf-t)", chip: "rgba(192,120,74,0.16)", chipBorder: "rgba(192,120,74,0.32)", icon: "warning" }
      : { line: "var(--mnf-gold)", chip: "rgba(160,120,32,0.14)", chipBorder: "rgba(160,120,32,0.28)", icon: "info" };

  return (
    <GlassCard
      padding={18}
      data-testid="card-safety-advisory"
      style={{
        background: "linear-gradient(135deg, rgba(245,240,232,0.96) 0%, rgba(192,120,74,0.08) 100%)",
        border: `1px solid ${accent.chipBorder}`,
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="shrink-0 inline-flex items-center justify-center"
          style={{
            width: 36, height: 36, borderRadius: 12,
            background: accent.chip, color: accent.line,
            border: `1px solid ${accent.chipBorder}`,
          }}
        >
          <Icon name={accent.icon} size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="mnf-eyebrow"
            style={{ color: accent.line, marginBottom: 4 }}
            data-testid="text-safety-eyebrow"
          >
            Season-aware advisory
          </p>
          <p
            className="mnf-serif"
            style={{ fontSize: 16, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.3 }}
            data-testid="text-safety-headline"
          >
            {headline}
          </p>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {advisories.map((a) => (
          <div
            key={a.flag}
            data-testid={`row-advisory-${a.flag}`}
            style={{
              padding: "12px 14px",
              borderRadius: 14,
              background: "rgba(255,255,255,0.6)",
              border: "1px solid rgba(30,74,53,0.06)",
            }}
          >
            <div className="flex items-center gap-2 mb-1.5">
              <span
                className="inline-flex items-center gap-1 mnf-body"
                style={{
                  background: a.severity === "avoid" ? "rgba(192,120,74,0.16)" : "rgba(160,120,32,0.14)",
                  color: a.severity === "avoid" ? "var(--mnf-t)" : "var(--mnf-gold)",
                  border: `1px solid ${a.severity === "avoid" ? "rgba(192,120,74,0.32)" : "rgba(160,120,32,0.28)"}`,
                  padding: "3px 9px",
                  borderRadius: 999,
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}
              >
                <Icon name={a.severity === "avoid" ? "warning" : "info"} size={11} />
                {a.severity === "avoid" ? "Set aside" : "Caution"}
              </span>
              <p
                className="mnf-serif"
                style={{ fontSize: 14, fontWeight: 500, color: "var(--mnf-fd)" }}
              >
                {a.label}
              </p>
            </div>
            <p
              className="mnf-body"
              style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.55 }}
            >
              {a.explanation}
            </p>
            <p
              className="mnf-body italic"
              style={{ fontSize: 12, color: "var(--mnf-fd)", lineHeight: 1.5, marginTop: 6 }}
            >
              <span style={{ fontWeight: 600, fontStyle: "normal", color: "var(--mnf-t)" }}>Try instead: </span>
              {a.alternative}
            </p>
          </div>
        ))}
      </div>

      <p
        className="mnf-body italic"
        style={{
          fontSize: 11.5,
          color: "var(--mnf-tg)",
          lineHeight: 1.55,
          marginTop: 14,
          textAlign: "center",
        }}
        data-testid="text-safety-footer"
      >
        Coach Kiley's plan and your provider always override.
      </p>
    </GlassCard>
  );
}
